import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useLocaleOptional } from '@/i18n/locale';
import { cn } from '@/lib/utils';

export interface StateBlockProps {
  /** Eyebrow naming the empty lens ("Your watchlist", "In progress"). */
  overline?: string;
  /** The one calm sentence, set in `text-headline`. */
  title: string;
  /** Optional longer body copy (reading-measure constrained). */
  body?: string;
  /** The single optional action — a `Button` or a `Link`. */
  action?: ReactNode;
  /**
   * Landmark role, caller-decided per ui-ux.md §7: `"status"` for an empty
   * state, `"alert"` for an error.
   */
  role?: 'status' | 'alert';
  /**
   * Vertical footprint: `page` (the default `py-16`) for a full-view state,
   * `inline` (`py-8`) for a region inside a larger panel.
   */
  size?: 'page' | 'inline';
  className?: string;
  /** Alternative to `action` — rendered in the same action slot. */
  children?: ReactNode;
}

/**
 * The one shared empty/error composition (ui-ux.md §7): overline +
 * `text-headline` line + optional body + optional single action, in a
 * consistent landmark role. An error (`role="alert"`) also gets a `danger`
 * `AlertCircle` inline before the title. Every new empty/error state is an
 * instance of this, not a bespoke block — the ~20 hand-rolled variants with
 * inconsistent roles are what this replaces.
 */
export function StateBlock({
  overline,
  title,
  body,
  action,
  role = 'status',
  size = 'page',
  className,
  children,
}: StateBlockProps) {
  const cta = action ?? children;
  return (
    <div
      role={role}
      className={cn(
        'flex flex-col items-start gap-4',
        size === 'inline' ? 'py-8' : 'py-16',
        className,
      )}
    >
      {overline ? <p className="text-kicker text-ink-secondary">{overline}</p> : null}
      <div className="flex items-center gap-2">
        {role === 'alert' ? <AlertCircle className="h-5 w-5 shrink-0 text-danger" aria-hidden /> : null}
        <p className="text-headline text-ink">{title}</p>
      </div>
      {body ? <p className="prose-measure text-body text-ink-secondary">{body}</p> : null}
      {cta ? <div className="mt-1 flex flex-wrap items-center gap-3">{cta}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  /** The one calm error sentence. */
  title: string;
  /** Optional longer body copy. */
  body?: string;
  /** Retry callback; when present a neutral Retry button is rendered. */
  onRetry?: () => void;
  /** Retry button label — defaults to `common.retry`. */
  retryLabel?: string;
  size?: 'page' | 'inline';
}

/**
 * The canonical error surface (ui-ux.md §7): a `role="alert"` `StateBlock`
 * with a neutral Retry button. Screen agents should route every error surface
 * through this instead of hand-rolling a kicker + headline + retry block.
 */
export function ErrorState({ title, body, onRetry, retryLabel, size }: ErrorStateProps) {
  const { t } = useLocaleOptional();
  return (
    <StateBlock
      role="alert"
      title={title}
      body={body}
      size={size}
      action={
        onRetry ? (
          <Button variant="neutral" onClick={onRetry}>
            {retryLabel ?? t('common.retry')}
          </Button>
        ) : undefined
      }
    />
  );
}
